import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();

  usePageMeta(
    "Privacy Policy — Ivy",
    "How Ivy Digital LLC collects, uses, stores, and protects your data."
  );

  return (
    <div className="min-h-screen bg-[#FAF9F5] text-[#5B655E] font-schibsted">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-[#FAF9F5]/90 backdrop-blur-sm border-b border-[#E5E1D6]">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-[#5B655E] hover:text-[#1F2823] transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <img src="/Ivy-logo.png" alt="Ivy" className="h-6 w-auto" />
          </button>
          <a
            href="/terms"
            className="text-sm text-[#5B655E] hover:text-[#0F766E] transition-colors"
          >
            Terms of Service
          </a>
        </div>
      </nav>

      {/* Content */}
      <div className="container mx-auto px-6 py-16 max-w-3xl">
        <h1 className="font-spectral text-4xl font-medium text-[#1F2823] mb-2">
          Privacy Policy
        </h1>
        <p className="text-[#8A938C] mb-12">Last updated: January 29, 2026</p>

        <div className="space-y-10 text-[15px] leading-relaxed">
          <section>
            <h2 className="font-spectral text-xl font-medium text-[#1F2823] mb-3">
              1. Introduction
            </h2>
            <p>
              Ivy Digital LLC ("Company," "we," "us," or "our") operates the Ivy
              platform available at ivytrack.app (the "Service"). This Privacy
              Policy describes how we collect, use, store, and disclose your
              information when you use our Service.
            </p>
            <p className="mt-3">
              By accessing or using the Service, you agree to this Privacy
              Policy. If you do not agree, you may not use the Service.
            </p>
          </section>

          <section>
            <h2 className="font-spectral text-xl font-medium text-[#1F2823] mb-3">
              2. Information We Collect
            </h2>

            <h3 className="text-[#1F2823] font-semibold mt-4 mb-2">
              2.1 Account Information
            </h3>
            <p>
              When you create an account, we collect your name, email address,
              and authentication credentials. If you sign in via Microsoft, we
              receive your profile information from Microsoft.
            </p>

            <h3 className="text-[#1F2823] font-semibold mt-4 mb-2">
              2.2 Calendar Data
            </h3>
            <p>
              If you connect your Microsoft Outlook calendar, we access calendar
              event data (event titles, attendees, times, and durations) to
              identify and import expert interview records. We do not access
              email content, attachments, or non-interview calendar events
              beyond what is necessary to identify interviews.
            </p>

            <h3 className="text-[#1F2823] font-semibold mt-4 mb-2">
              2.3 Interview and Transcript Data
            </h3>
            <p>
              You may upload interview transcripts, notes, expert details,
              project information, and related data. This data is provided
              entirely by you, and you are responsible for ensuring you have the
              right to upload it.
            </p>

            <h3 className="text-[#1F2823] font-semibold mt-4 mb-2">
              2.4 Usage Data
            </h3>
            <p>
              We automatically collect information about how you interact with
              the Service, including pages visited, features used, browser type,
              and IP address.
            </p>

            <h3 className="text-[#1F2823] font-semibold mt-4 mb-2">
              2.5 Cookies and Session Data
            </h3>
            <p>
              We use session cookies to maintain your authentication state.
              These cookies are essential for the Service to function and cannot
              be disabled.
            </p>
          </section>

          <section>
            <h2 className="font-spectral text-xl font-medium text-[#1F2823] mb-3">
              3. How We Use Your Information
            </h2>
            <p>We use the information we collect to:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Provide, operate, and maintain the Service</li>
              <li>Authenticate your identity and manage your account</li>
              <li>Synchronize calendar data to create interview records</li>
              <li>
                Process transcripts using artificial intelligence for cleaning
                and analysis
              </li>
              <li>
                Enable the chat feature to query across your interview data
              </li>
              <li>Respond to your inquiries and support requests</li>
              <li>Monitor and analyze usage trends to improve the Service</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="font-spectral text-xl font-medium text-[#1F2823] mb-3">
              4. Third-Party Service Providers
            </h2>
            <p>
              We may share your information with third-party service providers
              who assist us in operating the Service, including providers for:
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Cloud hosting and data storage</li>
              <li>Authentication and identity services</li>
              <li>AI and machine learning processing</li>
              <li>Analytics and performance monitoring</li>
            </ul>
            <p className="mt-3">
              These providers only have access to your information as necessary
              to perform their functions and are obligated to maintain its
              confidentiality. We do not sell, rent, or trade your personal
              information to any third party for marketing or advertising
              purposes.
            </p>
          </section>

          <section>
            <h2 className="font-spectral text-xl font-medium text-[#1F2823] mb-3">
              5. Data Storage and Security
            </h2>
            <p>
              Your data is stored in cloud-hosted databases with encryption at
              rest and in transit. We implement commercially reasonable security
              measures to protect your data, including secure session management,
              HTTPS encryption, and access controls.
            </p>
            <p className="mt-3">
              However, no method of electronic storage or transmission is 100%
              secure. We cannot guarantee absolute security of your data.
            </p>
          </section>

          <section>
            <h2 className="font-spectral text-xl font-medium text-[#1F2823] mb-3">
              6. Data Retention
            </h2>
            <p>
              We retain your data for as long as your account is active or as
              needed to provide the Service. If you request account deletion, we
              will delete your personal data and associated content within 30
              days, except where we are required to retain it by law.
            </p>
          </section>

          <section>
            <h2 className="font-spectral text-xl font-medium text-[#1F2823] mb-3">
              7. Your Rights
            </h2>
            <p>Depending on your jurisdiction, you may have the right to:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Access the personal data we hold about you</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Export your data in a portable format</li>
              <li>Object to or restrict certain processing of your data</li>
              <li>Withdraw consent where processing is based on consent</li>
            </ul>
            <p className="mt-3">
              To exercise any of these rights, contact us at{" "}
              <a
                href="mailto:support@ivytrack.app"
                className="text-[#0F766E] hover:text-[#0B5D57] underline"
              >
                support@ivytrack.app
              </a>
              . We will respond within 30 days.
            </p>
          </section>

          <section>
            <h2 className="font-spectral text-xl font-medium text-[#1F2823] mb-3">
              8. International Data Transfers
            </h2>
            <p>
              Your data may be processed in the United States or other
              jurisdictions where our service providers operate. By using the
              Service, you consent to the transfer of your data to these
              jurisdictions, which may have different data protection laws than
              your country of residence.
            </p>
          </section>

          <section>
            <h2 className="font-spectral text-xl font-medium text-[#1F2823] mb-3">
              9. Children's Privacy
            </h2>
            <p>
              The Service is not intended for individuals under 18 years of age.
              We do not knowingly collect personal information from children. If
              we learn that we have collected data from a child, we will delete
              it promptly.
            </p>
          </section>

          <section>
            <h2 className="font-spectral text-xl font-medium text-[#1F2823] mb-3">
              10. Changes to This Policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time. We will
              notify you of material changes by posting the updated policy on
              this page and updating the "Last updated" date. Your continued use
              of the Service after changes constitutes acceptance of the updated
              policy.
            </p>
          </section>

          <section>
            <h2 className="font-spectral text-xl font-medium text-[#1F2823] mb-3">
              11. Contact Us
            </h2>
            <p>
              If you have questions about this Privacy Policy, contact us at:
            </p>
            <p className="mt-2">
              Ivy Digital LLC
              <br />
              Email:{" "}
              <a
                href="mailto:support@ivytrack.app"
                className="text-[#0F766E] hover:text-[#0B5D57] underline"
              >
                support@ivytrack.app
              </a>
            </p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 border-t border-[#E5E1D6]">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <span className="text-[#8A938C] text-sm">
              &copy; {new Date().getFullYear()} Ivy Digital LLC
            </span>
            <div className="flex items-center gap-6">
              <a
                href="/terms"
                className="text-[#8A938C] hover:text-[#0F766E] text-sm transition-colors"
              >
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
